import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, CheckCircle } from 'lucide-react'
import { toast } from 'sonner'
import { jwtDecode } from 'jwt-decode'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'

import { useBranding, useResponses } from '@utils/api'
import { Response } from '@utils/api/aboboApiTypes'

// Lazy load SignatureWizard to defer loading heavy PDF dependencies (~3MB)
const SignatureWizard = lazy(() => import('@components/SignatureWizard'))

// Statuses that indicate the response is done and no longer editable
const DONE_STATUSES = ['completed', 'pending_review']
const isDone = (status: string) => DONE_STATUSES.includes(status)

type MyDocsSearch = {
  token?: string
}

export const Route = createFileRoute('/mydocs/')({
  component: MyDocs,
  validateSearch: (search: Record<string, unknown>): MyDocsSearch => {
    return {
      token: search.token as string | undefined,
    }
  },
})

function MyDocs() {
  const navigate = useNavigate()
  const { token } = useSearch({ from: '/mydocs/' })
  const { branding, isError } = useBranding()
  const { t, i18n, ready } = useTranslation()
  const {
    responses,
    isError: isResponsesError,
    isLoading: isResponsesLoading,
    mutate,
  } = useResponses({
    fetcherOptions: { headers: { Authorization: `Bearer ${token}` } },
    delayLoading: !token,
  })
  const [activeResponseId, setActiveResponseId] = useState<string | null>(null)

  const incompleteResponses = useMemo(
    () => responses?.filter((response) => !isDone(response.status)) || [],
    [responses]
  )

  const hasIncompleteResponses = useMemo(
    () => incompleteResponses.length > 0,
    [incompleteResponses]
  )

  useEffect(() => {
    if (!token) return

    try {
      const decodedToken: any = jwtDecode(token)
      if (decodedToken && decodedToken.language) {
        i18n.changeLanguage(decodedToken.language)
      }
    } catch (error) {
      console.error('Failed to decode token', error)
    }
  }, [token, i18n])

  const onResponseCompleted = () => {
    mutate()
    setActiveResponseId(null)
    toast.success(t('form.submissionSavedToast'))
  }

  const renderResponse = (response: Response) => {
    const completed = isDone(response.status)
    return (
      <Card key={response.id} className="w-full mb-2">
        <CardContent className="">
          <div className="flex items-center gap-3">
            <CheckCircle
              className={`w-5 h-5 ${completed ? 'text-green-500' : 'text-gray-400'}`}
            />
            <span className="flex-1 text-sm font-medium">{response.title}</span>
            <Button
              onClick={() => setActiveResponseId(response.id)}
              disabled={completed}
              variant={completed ? 'outline' : 'default'}
              size="sm"
            >
              {completed ? t('form.buttons.completed') : t('form.buttons.simpleStart')}
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  const renderContent = () => {
    if (hasIncompleteResponses) {
      return (
        <div className="w-full p-4">
          <h2 className="mb-2 text-xl font-medium">
            {t('form.formListTitle', { count: incompleteResponses?.length })}
          </h2>
          <div className="space-y-2">
            {responses?.map((response) => renderResponse(response))}
          </div>
        </div>
      )
    }

    return (
      <div className="flex flex-col items-center gap-4">
        <h2 className="text-lg font-medium">{t('form.noForms')}</h2>
        <CheckCircle className="w-24 h-24 text-green-500" />
      </div>
    )
  }

  const renderMyDocs = () => {
    return (
      <div className="py-4 px-2 bg-white border-b border-[#ffa40a] max-w-lg w-full rounded-2xl">
        <div className="flex flex-col items-start">
          <img
            className="pt-[5px] pb-4 w-full h-[100px] object-contain sticky top-0 bg-white border-b border-gray-300"
            alt={`${branding?.name}'s Logo`}
            src={branding?.logo || ''}
          />
          <div className="w-full p-2">
            {isResponsesLoading || !ready ? (
              <div className="flex justify-center items-center min-h-[200px]">
                <Loader2 className="animate-spin w-8 h-8 text-gray-500" />
              </div>
            ) : (
              renderContent()
            )}
          </div>
        </div>
      </div>
    )
  }

  if (isError || isResponsesError) {
    navigate({ to: '/' })
    return null
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-2 py-4 w-full transition-opacity duration-300">
      {activeResponseId ? (
        <Suspense
          fallback={
            <div className="flex justify-center items-center min-h-[200px]">
              <Loader2 className="animate-spin w-8 h-8 text-gray-500" />
            </div>
          }
        >
          <SignatureWizard
            responseId={activeResponseId}
            onComplete={onResponseCompleted}
            token={token ?? ''}
          />
        </Suspense>
      ) : (
        renderMyDocs()
      )}
    </div>
  )
}
